// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { View, Button, Icons, GSGrid, Pagination, Row, Col } from "@components";
import type { State } from "@store/reducers";
import type { PaginationExtendedType } from "@types";
import { formatDate } from "@transformers/date";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import type {
  SpecialContact,
  SpecialContactListOptions,
} from "@modules/service/reducer/special-contacts/types";
import type { SpecialContactsType } from "@modules/service/reducer/special-contacts-types/types";
import {
  SpecialContactsTypeFilter,
  SpecialContactsAddModal,
  SpecialContactsEditModal,
  SpecialContactsRemoveModal,
} from "@modules/service/components";
import { Link } from "react-router-dom";
import { route } from "@routes/helper";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  list: Array<SpecialContact>,
  listOptions: SpecialContactListOptions,
  types: Array<SpecialContactsType>,
  pagination: PaginationExtendedType,
  current: ?SpecialContact,
  showAddModal: boolean,
  showEditModal: boolean,
  showDeleteModal: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getList: typeof actionCreators.GetSpecialContacts,
  update: typeof actionCreators.UpdateSpecialContact,
  remove: typeof actionCreators.RemoveSpecialContact,
  setCurrent: typeof actionCreators.SetCurrentSpecialContact,
  setShowAddModal: typeof actionCreators.SetShowAddSpecialContactModal,
  setShowEditModal: typeof actionCreators.SetShowEditSpecialContactModal,
  setShowDeleteModal: typeof actionCreators.SetShowDeleteSpecialContactModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class SpecialContactsList extends React.Component<Props> {
  componentDidMount() {
    this.getList();
  }

  getList = (options: { page?: number, search?: Object, sort?: Object } = {}) => {
    this.props.getList({
      ...{ ...this.props.listOptions, ...options },
      clientId: this.props.clientId,
    });
  };

  setItemStatus = (item: SpecialContact, active: boolean) => {
    this.props.update({
      clientId: this.props.clientId,
      specialContactId: item.id,
      data: { ...item, isActive: active },
      options: this.props.listOptions,
    });
  };

  updatePage = (value: number) => {
    this.getList({ page: value });
  };

  updateFilter = (value: number) => {
    this.getList({ page: 1, search: value ? { type: value } : {} });
  };

  updateSort = (value: Object) => {
    this.getList({ page: 1, sort: value });
  };

  showAddModal = (item: ?SpecialContact) => () => {
    this.props.setCurrent(item);
    this.props.setShowAddModal(true);
  };

  showEditModal = (item: ?SpecialContact) => () => {
    this.props.setCurrent(item);
    this.props.setShowEditModal(true);
  };

  showDeletePrompt = (item: SpecialContact) => () => {
    this.props.setCurrent(item);
    this.props.setShowDeleteModal(true);
  };

  closeDeletePrompt = () => {
    this.props.setCurrent(null);
    this.props.setShowDeleteModal(false);
  };

  handleRemove = () => {
    this.props.remove({
      clientId: this.props.clientId,
      specialContactId: (this.props.current || {}).id,
    });
  };

  get columns() {
    return [
      {
        key: "id",
        name: this.props.t("ID"),
        align: "left",
      },
      {
        key: "type",
        name: this.props.t("TYPE"),
        align: "left",
        renderCell: ({ row }: { row: SpecialContact }) => (row.type || {}).name,
        sortable: true,
      },
      {
        key: "employeeReference",
        name: this.props.t("EMPLOYEE_REF"),
        align: "left",
        renderCell: ({ row }: { row: SpecialContact }) => (
          <Link
            className="text-charcoal-dark"
            target={"_blank"}
            to={route("REPORTING_EMPLOYEE_DETAILS_PATH", {
              clientId: row.organisationalUnit?.clientId,
              employeeId: row.employee.id,
            })}
          >
            {(row.employee || {}).employeeRefPrimary}
          </Link>
        ),
        sortable: true,
      },
      {
        key: "employeeName",
        name: this.props.t("EMPLOYEE_NAME"),
        align: "left",
        renderCell: ({ row }: { row: SpecialContact }) => (row.employee || {}).name,
        sortable: true,
      },
      {
        key: "department",
        name: this.props.t("DEPARTMENT"),
        align: "left",
        renderCell: ({ row }: { row: SpecialContact }) => (row.organisationalUnit || {}).name,
      },
      {
        key: "createdAt",
        name: this.props.t("DATE_ADDED"),
        renderCell: ({ row }: { row: SpecialContact }) => formatDate(row.createdAt),
        sortable: true,
      },
      {
        key: "isActive",
        name: this.props.t("STATUS"),
        align: "center",
        onCellClick: (row: SpecialContact) => this.setItemStatus(row, !row.isActive),
        renderCell: ({ row }: { row: SpecialContact }) => (
          <div className="d-flex justify-content-end align-items-center">
            <div className={`mr-1 ${row.isActive ? "active-badge" : "inactive-badge"}`}>
              {row.isActive ? this.props.t("ACTIVE") : this.props.t("INACTIVE")}
            </div>
            {row.isActive ? (
              <Icons.EnabledIcon className="pointer" />
            ) : (
              <Icons.DisabledIcon className="pointer" />
            )}
          </div>
        ),
        sortable: true,
      },
      {
        key: "edit",
        width: 50,
        name: this.props.t("EDIT"),
        renderCell: ({ row }: { row: SpecialContact }) => (
          <span className="material-icons pointer font-size-26" onClick={this.showEditModal(row)}>
            edit
          </span>
        ),
      },
      {
        key: "delete",
        width: 50,
        name: this.props.t("delete"),
        renderCell: ({ row }: { row: SpecialContact }) => (
          <span
            className="material-icons pointer font-size-26"
            onClick={this.showDeletePrompt(row)}
          >
            delete
          </span>
        ),
      },
    ];
  }

  render() {
    return (
      <View>
        <View margin="0 0 1rem 0" justifyContent="space-between" alignItems="end" direction="row">
          <View width="30%">
            <SpecialContactsTypeFilter
              types={this.props.types}
              labelValue="TYPE"
              defaultValue={((this.props.listOptions || {}).search || {}).type || 0}
              showEmptyOption
              onChange={this.updateFilter}
            />
          </View>
          <Button
            materialIcon="add_box"
            onClick={this.showAddModal()}
            label={this.props.t("ADD")}
          />
        </View>
        <GSGrid
          rows={this.props.list}
          columns={this.columns}
          onSortChange={this.updateSort}
          sort={this.props.listOptions.sort}
        />
        {this.props.pagination && (
          <Pagination
            className="mx-auto d-block"
            currentPage={this.props.pagination.pageNumber}
            totalPages={this.props.pagination.totalPages}
            onChange={this.updatePage}
          />
        )}
        {this.props.showAddModal && (
          <SpecialContactsAddModal defaultStep={1} clientId={this.props.clientId} />
        )}
        {this.props.showEditModal && (
          <SpecialContactsEditModal defaultStep={3} clientId={this.props.clientId} />
        )}
        {this.props.showDeleteModal && (
          <SpecialContactsRemoveModal
            specialContact={this.props.current}
            onCancel={this.closeDeletePrompt}
            onSubmit={this.handleRemove}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State, ownProps): PropsFromState => {
  return {
    list: serviceSelectors.getSpecialContacts(state),
    listOptions: serviceSelectors.getSpecialContactsListOptions(state),
    pagination: serviceSelectors.getSpecialContactsPagination(state),
    types: serviceSelectors.getSpecialContactsTypes(state),
    current: serviceSelectors.getCurrentSpecialContact(state),
    showAddModal: serviceSelectors.getShowAddSpecialContactModal(state),
    showEditModal: serviceSelectors.getShowEditSpecialContactModal(state),
    showDeleteModal: serviceSelectors.getShowDeleteSpecialContactModal(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getList: actionCreators.GetSpecialContacts,
  update: actionCreators.UpdateSpecialContact,
  remove: actionCreators.RemoveSpecialContact,
  setCurrent: actionCreators.SetCurrentSpecialContact,
  setShowAddModal: actionCreators.SetShowAddSpecialContactModal,
  setShowEditModal: actionCreators.SetShowEditSpecialContactModal,
  setShowDeleteModal: actionCreators.SetShowDeleteSpecialContactModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(SpecialContactsList));
